import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';

import { useAuth } from '@/app/auth/context';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import {
  FIELD_LABELS,
  FIELDS_BY_TYPE,
  SURVEY_TYPE_LABELS,
  createPresidentResponse,
  deletePresidentResponse,
  fetchPresidentResponse,
  fetchPresidentResponses,
  fetchPresidentRoundLabels,
  updatePresidentResponse,
  type PresidentFieldKey,
  type PresidentResponseDetail,
  type PresidentSurveyType,
} from '@/features/presidentSurveys/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso } from '@/lib/datetime';

/**
 * 会員詳細の「社長アンケート」タブ（指示書 §6・§12 M7 / D-146・D-342）。
 * 事務局: 一覧・代理入力・編集・削除。開示範囲はシステム固定（事務局のみ）。
 * 理事・講師／サクセス担当は office_only 以外の旧データがあれば閲覧可（API側 / §0-6）。
 * 設問マスタは廃止し、固定6フィールド（種別で3項目ずつ）に固定した（D-146）。
 *
 * `readOnly` を渡すと、代理入力・修正・削除の操作を出さず閲覧だけにする。
 * 会員詳細は「見る」場所、`/members/:id/president-survey` が「入れる」場所という
 * 分担にするため（D-316）。
 */
export function MemberPresidentSurveyTab({
  memberId,
  readOnly = false,
}: {
  memberId: string;
  readOnly?: boolean;
}): ReactElement {
  const { user } = useAuth();
  const isOffice = user?.active_role === 'office' && !readOnly;
  const query = useQuery({
    queryKey: ['president-responses', memberId],
    queryFn: () => fetchPresidentResponses(memberId),
  });

  const [openId, setOpenId] = useState<string | null>(null);
  const [entryOpen, setEntryOpen] = useState(false);

  if (query.isPending) return <p className="text-wptext-muted">読み込み中…</p>;
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 403) {
      return <p className="text-wptext-muted">社長アンケートを閲覧する権限がありません。</p>;
    }
    return <p className="text-wpnotice-error">読み込みに失敗しました。</p>;
  }

  const openSummary = query.data.find((s) => s.id === openId) ?? null;

  return (
    <div className="flex flex-col gap-3">
      {(isOffice || readOnly) && user?.active_role === 'office' && (
        <p className="text-wptext-muted">
          社長アンケートの閲覧は事務局のみです。開示範囲はシステム側で固定されています。
        </p>
      )}
      {isOffice && (
        <div>
          <button
            type="button"
            onClick={() => setEntryOpen((open) => !open)}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            {entryOpen ? '入力を閉じる' : '+ ヒアリング結果を代理入力'}
          </button>
          {entryOpen && <ManualEntryForm memberId={memberId} onDone={() => setEntryOpen(false)} />}
        </div>
      )}

      {query.data.length === 0 && (
        <p className="text-wptext-muted">登録された社長アンケートはありません。</p>
      )}

      {query.data.map((summary) => (
        <div key={summary.id} className="rounded-sm border border-wpborder bg-white p-3">
          <button
            type="button"
            onClick={() => setOpenId(summary.id)}
            className="flex w-full items-center justify-between text-left"
          >
            <span className="font-semibold text-wptext-strong">
              {SURVEY_TYPE_LABELS[summary.survey_type]} — {summary.survey_round}
            </span>
            <span className="text-wptext-muted">
              聴取日:{' '}
              {summary.answered_at
                ? new Date(summary.answered_at).toLocaleDateString('ja-JP')
                : '—'}{' '}
              ・ 聴取: {summary.interviewer_name ?? '—'}
            </span>
          </button>
        </div>
      ))}

      {/* 一覧の行を押した結果はモーダルで出す（D-094）。一覧の下にインライン展開すると、
          件数が多いとき押しても画面外に反応が見えず「動かない」ように見える
          （実機フィードバック / D-164） */}
      <Modal
        open={openSummary !== null}
        title={
          openSummary
            ? `${SURVEY_TYPE_LABELS[openSummary.survey_type]} — ${openSummary.survey_round}`
            : ''
        }
        onClose={() => setOpenId(null)}
        wide
      >
        {openId && (
          <ResponseDetail
            responseId={openId}
            memberId={memberId}
            canEdit={isOffice}
            onDeleted={() => setOpenId(null)}
          />
        )}
      </Modal>
    </div>
  );
}

function ResponseDetail({
  responseId,
  memberId,
  canEdit,
  onDeleted,
}: {
  responseId: string;
  memberId: string;
  canEdit: boolean;
  onDeleted: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['president-response', responseId],
    queryFn: () => fetchPresidentResponse(responseId),
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);

  if (query.isPending) return <p className="mt-2 text-wptext-muted">読み込み中…</p>;
  if (query.isError) return <p className="mt-2 text-wpnotice-error">読み込みに失敗しました。</p>;

  const detail: PresidentResponseDetail = query.data;
  const fields = FIELDS_BY_TYPE[detail.survey_type];

  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['president-response', responseId] });
    await queryClient.invalidateQueries({ queryKey: ['president-responses', memberId] });
  }

  async function onDelete(): Promise<void> {
    if (!window.confirm('この回答を削除します。よろしいですか？')) return;
    setBusy(true);
    setError(null);
    try {
      await deletePresidentResponse(responseId);
      await refresh();
      onDeleted();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '削除に失敗しました。');
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 border-t border-wpborder pt-3">
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {editing ? (
        <EditForm
          detail={detail}
          onDone={() => {
            setEditing(false);
            void refresh();
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <>
          <dl className="flex flex-col gap-3">
            {fields.map((key) => (
              <div key={key}>
                <dt className="font-semibold text-wptext-strong">{FIELD_LABELS[key]}</dt>
                <dd className="mt-1 whitespace-pre-wrap">
                  {detail[key] === null || detail[key] === ''
                    ? '—'
                    : key === 'satisfaction_score'
                      ? `${detail[key]} / 5`
                      : String(detail[key])}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-wptext-muted">
            {canEdit ? (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setEditing(true)}
                  className="text-wpaccent hover:underline disabled:opacity-50"
                >
                  内容を編集
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onDelete()}
                  className="text-wpnotice-error hover:underline disabled:opacity-50"
                >
                  削除
                </button>
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/** 種別に応じた3項目の入力欄（代理入力と編集で共用） */
function FieldInputs({
  surveyType,
  values,
  onChange,
}: {
  surveyType: PresidentSurveyType;
  values: Partial<Record<PresidentFieldKey, string>>;
  onChange: (key: PresidentFieldKey, value: string) => void;
}): ReactElement {
  return (
    <div className="flex flex-col gap-3">
      {FIELDS_BY_TYPE[surveyType].map((key) =>
        key === 'satisfaction_score' ? (
          <label key={key} className="block">
            <span className="mb-1.5 block text-wptext-strong">{FIELD_LABELS[key]}</span>
            <select
              value={values[key] ?? ''}
              onChange={(e) => onChange(key, e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">未回答</option>
              {[1, 2, 3, 4, 5].map((n) => (
                <option key={n} value={String(n)}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        ) : (
          <label key={key} className="block">
            <span className="mb-1.5 block text-wptext-strong">{FIELD_LABELS[key]}</span>
            <textarea
              value={values[key] ?? ''}
              onChange={(e) => onChange(key, e.target.value)}
              rows={3}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            />
          </label>
        ),
      )}
    </div>
  );
}

function fieldPayload(
  surveyType: PresidentSurveyType,
  values: Partial<Record<PresidentFieldKey, string>>,
): Record<string, string | number | null> {
  const payload: Record<string, string | number | null> = {};
  for (const key of FIELDS_BY_TYPE[surveyType]) {
    const raw = values[key] ?? '';
    if (key === 'satisfaction_score') {
      payload[key] = raw === '' ? null : Number(raw);
    } else {
      payload[key] = raw === '' ? null : raw;
    }
  }
  return payload;
}

function EditForm({
  detail,
  onDone,
  onCancel,
}: {
  detail: PresidentResponseDetail;
  onDone: () => void;
  onCancel: () => void;
}): ReactElement {
  const [values, setValues] = useState<Partial<Record<PresidentFieldKey, string>>>(() => {
    const initial: Partial<Record<PresidentFieldKey, string>> = {};
    for (const key of FIELDS_BY_TYPE[detail.survey_type]) {
      initial[key] = detail[key] === null ? '' : String(detail[key]);
    }
    return initial;
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSave(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await updatePresidentResponse(detail.id, fieldPayload(detail.survey_type, values));
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '更新に失敗しました。');
      setBusy(false);
    }
  }

  return (
    <div>
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      <FieldInputs
        surveyType={detail.survey_type}
        values={values}
        onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
      />
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => void onSave()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          保存する
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="btn-secondary"
        >
          キャンセル
        </button>
      </div>
    </div>
  );
}

function ManualEntryForm({
  memberId,
  onDone,
}: {
  memberId: string;
  onDone: () => void;
}): ReactElement {
  const queryClient = useQueryClient();
  const roundsQuery = useQuery({
    queryKey: ['president-round-labels'],
    queryFn: fetchPresidentRoundLabels,
  });

  const [surveyType, setSurveyType] = useState<PresidentSurveyType>('start_expectation');
  const [surveyRound, setSurveyRound] = useState('');
  const [answeredAt, setAnsweredAt] = useState('');
  const [interviewer, setInterviewer] = useState('');
  const [values, setValues] = useState<Partial<Record<PresidentFieldKey, string>>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await createPresidentResponse({
        member_id: memberId,
        survey_type: surveyType,
        survey_round: surveyRound.trim(),
        answered_at: datetimeLocalToIso(answeredAt),
        interviewer_name: interviewer,
        ...fieldPayload(surveyType, values),
      });
      await queryClient.invalidateQueries({ queryKey: ['president-responses', memberId] });
      await queryClient.invalidateQueries({ queryKey: ['president-round-labels'] });
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '登録に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-3 rounded-sm border border-wpborder bg-white p-3">
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">種別</span>
          <select
            value={surveyType}
            onChange={(e) => setSurveyType(e.target.value as PresidentSurveyType)}
            className="rounded-sm border border-wpborder px-3 py-2"
          >
            {Object.entries(SURVEY_TYPE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">実施回（例: 3期 期中）</span>
          <input
            value={surveyRound}
            onChange={(e) => setSurveyRound(e.target.value)}
            list="president-round-labels"
            placeholder="自由に入力できます"
            className="rounded-sm border border-wpborder px-3 py-2"
          />
          {/* 過去に入力した実施回を候補に出す（表記の揺れを防ぐ）。マスタ管理はしない（D-146） */}
          <datalist id="president-round-labels">
            {(roundsQuery.data ?? []).map((label) => (
              <option key={label} value={label} />
            ))}
          </datalist>
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">聴取日</span>
          <input
            type="datetime-local"
            value={answeredAt}
            onChange={(e) => setAnsweredAt(e.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">聴取者（事務局担当）</span>
          <input
            value={interviewer}
            onChange={(e) => setInterviewer(e.target.value)}
            className="rounded-sm border border-wpborder px-3 py-2"
          />
        </label>
      </div>

      <div className="mt-3">
        <FieldInputs
          surveyType={surveyType}
          values={values}
          onChange={(key, value) => setValues((prev) => ({ ...prev, [key]: value }))}
        />
      </div>

      <div className="mt-3">
        <button
          type="button"
          disabled={busy || !surveyRound.trim() || !answeredAt || !interviewer}
          onClick={() => void onSubmit()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          登録する
        </button>
      </div>
    </div>
  );
}
